import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';

// Import all locales
import it from '../locales/it.json';
import en from '../locales/en.json';
import de from '../locales/de.json';
import fr from '../locales/fr.json';
import es from '../locales/es.json';
import pt from '../locales/pt.json';
import ro from '../locales/ro.json';
import pl from '../locales/pl.json';

const locales = { it, en, de, fr, es, pt, ro, pl };

export const SUPPORTED_LANGUAGES = [
  { code: 'it', name: 'Italiano', flag: '🇮🇹' },
  { code: 'en', name: 'English', flag: '🇬🇧' },
  { code: 'de', name: 'Deutsch', flag: '🇩🇪' },
  { code: 'fr', name: 'Français', flag: '🇫🇷' },
  { code: 'es', name: 'Español', flag: '🇪🇸' },
  { code: 'pt', name: 'Português', flag: '🇵🇹' },
  { code: 'ro', name: 'Română', flag: '🇷🇴' },
  { code: 'pl', name: 'Polski', flag: '🇵🇱' }
];

// Route mappings for each language
export const ROUTE_MAPPINGS = {
  it: {
    home: '',
    calculator: 'calcolo',
    reverse: 'inverso',
    verify: 'verifica',
    healthCard: 'tessera-sanitaria',
    municipalities: 'comuni',
    howItWorks: 'come-funziona',
    faq: 'faq',
    guide: 'guida-codice-fiscale',
    omocodia: 'omocodia',
    privacy: 'privacy',
    cookie: 'cookie',
    terms: 'termini',
    accessibility: 'accessibilita',
    about: 'chi-siamo',
    contact: 'contatti'
  },
  en: {
    home: '',
    calculator: 'calculator',
    reverse: 'reverse',
    verify: 'verify',
    healthCard: 'health-card',
    municipalities: 'municipalities',
    howItWorks: 'how-it-works',
    faq: 'faq',
    guide: 'guide',
    omocodia: 'omocodia',
    privacy: 'privacy',
    cookie: 'cookie',
    terms: 'terms',
    accessibility: 'accessibility',
    about: 'about',
    contact: 'contact'
  },
  de: {
    home: '',
    calculator: 'rechner',
    reverse: 'umkehren',
    verify: 'pruefen',
    healthCard: 'gesundheitskarte',
    municipalities: 'gemeinden',
    howItWorks: 'wie-funktioniert',
    faq: 'faq',
    guide: 'anleitung',
    omocodia: 'omocodia',
    privacy: 'datenschutz',
    cookie: 'cookie',
    terms: 'nutzungsbedingungen',
    accessibility: 'barrierefreiheit',
    about: 'about',
    contact: 'contact'
  },
  fr: {
    home: '',
    calculator: 'calculateur',
    reverse: 'inverse',
    verify: 'verifier',
    healthCard: 'carte-sante',
    municipalities: 'communes',
    howItWorks: 'comment-ca-marche',
    faq: 'faq',
    guide: 'guide',
    omocodia: 'omocodia',
    privacy: 'confidentialite',
    cookie: 'cookies',
    terms: 'conditions',
    accessibility: 'accessibilite',
    about: 'about',
    contact: 'contact'
  },
  es: {
    home: '',
    calculator: 'calculadora',
    reverse: 'inverso',
    verify: 'verificar',
    healthCard: 'tarjeta-sanitaria',
    municipalities: 'municipios',
    howItWorks: 'como-funciona',
    faq: 'faq',
    guide: 'guia',
    omocodia: 'omocodia',
    privacy: 'privacidad',
    cookie: 'cookies',
    terms: 'terminos',
    accessibility: 'accesibilidad',
    about: 'about',
    contact: 'contact'
  },
  pt: {
    home: '',
    calculator: 'calculadora',
    reverse: 'inverso',
    verify: 'verificar',
    healthCard: 'cartao-saude',
    municipalities: 'municipios',
    howItWorks: 'como-funciona',
    faq: 'faq',
    guide: 'guia',
    omocodia: 'omocodia',
    privacy: 'privacidade',
    cookie: 'cookies',
    terms: 'termos',
    accessibility: 'acessibilidade',
    about: 'about',
    contact: 'contact'
  },
  ro: {
    home: '',
    calculator: 'calculator',
    reverse: 'invers',
    verify: 'verifica',
    healthCard: 'card-sanatate',
    municipalities: 'comune',
    howItWorks: 'cum-functioneaza',
    faq: 'faq',
    guide: 'ghid',
    omocodia: 'omocodia',
    privacy: 'confidentialitate',
    cookie: 'cookies',
    terms: 'termeni',
    accessibility: 'accesibilitate',
    about: 'about',
    contact: 'contact'
  },
  pl: {
    home: '',
    calculator: 'kalkulator',
    reverse: 'odwrotny',
    verify: 'weryfikuj',
    healthCard: 'karta-zdrowia',
    municipalities: 'gminy',
    howItWorks: 'jak-dziala',
    faq: 'faq',
    guide: 'przewodnik',
    omocodia: 'omokodia',
    privacy: 'prywatnosc',
    cookie: 'cookies',
    terms: 'regulamin',
    accessibility: 'dostepnosc',
    about: 'about',
    contact: 'contact'
  }
};

// Helper to get language from URL path
export const getLanguageFromPath = (pathname) => {
  const segments = pathname.split('/').filter(Boolean);
  const firstSegment = segments[0];
  
  if (firstSegment && locales[firstSegment]) {
    return firstSegment;
  }
  return 'it'; // Default to Italian
};

// Helper to get route key from path
export const getRouteKeyFromPath = (pathname, lang) => {
  const segments = pathname.split('/').filter(Boolean);
  const routeSegment = lang === 'it' ? segments[0] : segments[1];
  
  if (!routeSegment) return 'home';
  
  const mappings = ROUTE_MAPPINGS[lang];
  for (const [key, value] of Object.entries(mappings)) {
    if (value === routeSegment) return key;
  }
  return 'home';
};

// Helper to build path for a language
export const buildPath = (lang, routeKey, queryString = '') => {
  const route = ROUTE_MAPPINGS[lang]?.[routeKey] || '';
  const langPrefix = lang === 'it' ? '' : `/${lang}`;
  const path = route ? `${langPrefix}/${route}` : langPrefix || '/';
  return queryString ? `${path}?${queryString}` : path;
};

// Get initial language from URL
const getInitialLocale = () => {
  if (typeof window === 'undefined') return 'it';
  
  const segments = window.location.pathname.split('/').filter(Boolean);
  const urlLang = segments[0];
  
  if (urlLang && locales[urlLang]) {
    return urlLang;
  }
  
  // During build-time prerender (react-snap/puppeteer set navigator.webdriver), localStorage is
  // shared across all 442 routes and polluted by processing order. Derive the locale purely from the
  // URL so each prerendered page reflects its own path's language ('it' for no prefix), not the
  // previously processed route's. Real users (navigator.webdriver undefined) keep the fallback below.
  if (navigator.webdriver) {
    return 'it';
  }

  // Check localStorage as fallback for root path (real users only)
  const savedLang = localStorage.getItem('cf_locale');
  if (savedLang && locales[savedLang]) {
    return savedLang;
  }
  
  return 'it';
};

const I18nContext = createContext(null);

export const I18nProvider = ({ children }) => {
  const initialLocale = getInitialLocale();
  const [locale, setLocale] = useState(initialLocale);
  const [messages, setMessages] = useState(locales[initialLocale] || locales.it);
  const [isInitialized, setIsInitialized] = useState(true);

  // Sync localStorage on mount
  useEffect(() => {
    localStorage.setItem('cf_locale', locale);
    document.documentElement.lang = locale;
  }, []);

  useEffect(() => {
    if (isInitialized) {
      setMessages(locales[locale] || locales.it);
      localStorage.setItem('cf_locale', locale);
      document.documentElement.lang = locale;
    }
  }, [locale, isInitialized]);

  const t = useCallback((key, params = {}) => {
    const keys = key.split('.');
    let value = messages;
    
    for (const k of keys) {
      value = value?.[k];
    }
    
    if (typeof value !== 'string') {
      // Try fallback to Italian
      let fallback = locales.it;
      for (const k of keys) {
        fallback = fallback?.[k];
      }
      if (typeof fallback === 'string') {
        value = fallback;
      } else {
        console.warn(`Translation key not found: ${key}`);
        return key;
      }
    }
    
    // Replace params
    return Object.entries(params).reduce(
      (str, [key, val]) => str.replace(new RegExp(`{{${key}}}`, 'g'), val),
      value
    );
  }, [messages]);

  const changeLocale = useCallback((newLocale, navigate) => {
    if (locales[newLocale] && newLocale !== locale) {
      // Get current route key
      const currentRouteKey = getRouteKeyFromPath(window.location.pathname, locale);
      const queryString = window.location.search.replace('?', '');
      
      // Build new path
      const newPath = buildPath(newLocale, currentRouteKey, queryString);
      
      setLocale(newLocale);
      
      // Navigate to new URL if navigate function is provided
      if (navigate) {
        navigate(newPath);
      } else {
        window.location.href = newPath;
      }
    }
  }, [locale]);

  return (
    <I18nContext.Provider value={{ 
      locale, 
      t, 
      changeLocale, 
      messages,
      buildPath,
      getRouteKeyFromPath,
      isInitialized
    }}>
      {children}
    </I18nContext.Provider>
  );
};

export const useI18n = () => {
  const context = useContext(I18nContext);
  if (!context) {
    throw new Error('useI18n must be used within I18nProvider');
  }
  return context;
};

// Hook to sync locale with URL
export const useLocaleFromUrl = () => {
  const location = useLocation();
  const { locale, changeLocale } = useI18n();
  
  useEffect(() => {
    const urlLang = getLanguageFromPath(location.pathname);
    if (urlLang !== locale) {
      // Just update locale state, don't navigate
      if (locales[urlLang]) {
        // Direct state update without navigation
        localStorage.setItem('cf_locale', urlLang);
      }
    }
  }, [location.pathname]);
  
  return { locale };
};

export default I18nContext;
